/**
 * Stałe layoutów akapitów — 1:1 mirror `web/define.php` (linie 918-939).
 *
 * **Single source of truth** dla typu akapitu w React-CMS — używane przez
 * dispatcher `<Paragraph>` oraz typy discriminated union `ParagraphData`
 * w `@/lib/api/cms-catalog`.
 *
 * Numery NIE są arbitralne — backend (`IPARAGRAPH_CONTROLLER`) zwraca te
 * wartości w polu `layout` per akapit. Każda zmiana po stronie PHP musi
 * mieć odpowiednik tutaj (oba pliki muszą zostać zsynchronizowane).
 *
 * **System-wide reusable:** ten plik jest celowo poza `components/cms/Paragraphs/`
 * żeby każdy moduł CMS / okna / panel admina mógł go zaimportować bez
 * zależności od konkretnego renderera. Lista typów akapitów = część kontraktu
 * API Imperial, nie implementacja UI.
 */

/** Akapit tylko tekst. */
export const PARAGRAPH_TEXT_LAYOUT = 1;

/** Tekst + obrazy po lewej. */
export const PARAGRAPH_TEXT_IMAGES_LEFT_LAYOUT = 2;
/** Tekst + obrazy po prawej. */
export const PARAGRAPH_TEXT_IMAGES_RIGHT_LAYOUT = 3;
/** Tekst + obrazy nad tekstem. */
export const PARAGRAPH_TEXT_IMAGES_TOP_LAYOUT = 4;
/** Tekst + obrazy pod tekstem. */
export const PARAGRAPH_TEXT_IMAGES_BOT_LAYOUT = 5;

/** 2 kolumny tekstu (text|text_1). */
export const PARAGRAPH_TWO_COLS_TEXT_LAYOUT = 14;
/** 2 kolumny: tekst | YouTube. */
export const PARAGRAPH_TWO_COLS_TEXT_VIDEO_LAYOUT = 6;
/** 2 kolumny: tabela HTML | tekst. */
export const PARAGRAPH_TWO_COLS_TABLE_TEXT_LAYOUT = 12;
/** 2 kolumny: tekst + galeria zdjęć obok. */
export const PARAGRAPH_TWO_COLS_TEXT_GALLERY_LAYOUT = 13;

/** Slider zdjęć (Swiper). */
export const PARAGRAPH_PREZENTATION_LAYOUT = 7;
/** Duże zdjęcie pojedyncze. */
export const PARAGRAPH_BIG_PHOTO_LAYOUT = 15;

/** 3 kolumny tekstu (text|text_1|text_2). */
export const PARAGRAPH_THREE_COLS_TEXT_LAYOUT = 16;
/** 3 kolumny: text | text_1 | photo. */
export const PARAGRAPH_THREE_COLS_TEXT_TEXT_PHOTO_LAYOUT = 8;
/** 3 kolumny: text | photo | text_1. */
export const PARAGRAPH_THREE_COLS_TEXT_PHOTO_TEXT_LAYOUT = 9;
/** 3 kolumny: photo | text | text_1. */
export const PARAGRAPH_THREE_COLS_PHOTO_TEXT_TEXT_LAYOUT = 10;
/** 3 kolumny z dużym YouTube. */
export const PARAGRAPH_THREE_COLS_BIG_VIDEO_LAYOUT = 11;

/** 2 boksy z obrazem + tytułem + CTA. */
export const PARAGRAPH_TWO_BOXES_LAYOUT = 17;
/** 3 boksy z obrazem + tytułem + CTA. */
export const PARAGRAPH_THREE_BOXES_LAYOUT = 18;

/** Formularz kontaktowy (email/phone/text + RODO). */
export const PARAGRAPH_CONTACT_FORM_LAYOUT = 22;
/** Mapa Leaflet z punktami GPX. */
export const PARAGRAPH_MAPS_LAYOUT = 23;
/** 2 kolumny: tekst | prezentacja zdjęć. */
export const PARAGRAPH_TWO_COLS_TEXT_PREZENTATION_LAYOUT = 24;
/** Karuzela / lista wpisów c_blog. */
export const PARAGRAPH_BLOG_POSTS_LAYOUT = 25;

/** Karta kontaktowa - osoba decyzyjna (zdjęcie tła + tytuł + imię/rola + CTA + telefon).
 *  Używane w `w_descriptive_window` w sidebarze artykułu. */
export const PARAGRAPH_CONTACT_PERSON_LAYOUT = 84;

/** Hero z CTA i statystykami - dedykowany akapit dla stron opisowych (strona główna).
 *  Semantyczne bloki: badge + tytuł (z inline accent) + subtitle + opis + 2 CTA + 3 stat boxy. */
export const PARAGRAPH_HERO_CTA_LAYOUT = 85;

/** Proces 4 kroki - dedykowany akapit "jak pracujemy".
 *  Eyebrow + title + lead + 4 karty krokow (num + name + desc + opcjonalny badge z tooltip). */
export const PARAGRAPH_PROCESS_STEPS_LAYOUT = 86;

/** CTA banner ze zdjeciem - dark image bg + eyebrow + title + desc + CTA + autor.
 *  Zastapienie editor_template "CTA-banner-ze-zdjeciem.html". */
export const PARAGRAPH_CTA_BANNER_LAYOUT = 83;

/** Produkt CTA - grid text/image, badge + title + desc + primary CTA + ghost btn z tooltipem.
 *  Zastapienie editor_template "Produkt-banner-ze-zdjeciem.html". */
export const PARAGRAPH_PRODUCT_CTA_LAYOUT = 87;

/** Cechy z ikonami - grid 3 kolumny x 2 wiersze = 6 kart {icon, name, desc}.
 *  Zastapienie editor_template "6-cech-z-ikonami.html". */
export const PARAGRAPH_FEATURES_ICONS_LAYOUT = 70;

/** Tekst + 4 boksy statystyk - grid 2-kol (tekst po lewej / 4 karty 2x2 po prawej).
 *  Zastapienie editor_template "Tekst-i-4-boksy.html". */
export const PARAGRAPH_TEXT_STATS_LAYOUT = 71;

/** Stack technologii - grid ikon (dynamiczny) + 3 karty AI modeli.
 *  Zastapienie editor_template "Stack-technologii.html". */
export const PARAGRAPH_TECH_STACK_LAYOUT = 72;

/** Service hero - hero subpage uslugi: eyebrow + tytul + lead + logotypy klientow
 *  po lewej / dark contact card z osoba decyzyjna + CTA + telefon po prawej. */
export const PARAGRAPH_SERVICE_HERO_LAYOUT = 73;

/** Zaawansowane funkcje 2-col - eyebrow + tytul + lead + grid 2-kol z 6 kart {icon, name, desc}.
 *  Wariant why-us (39) z lead pod tytulem + wezszy grid (2 kolumny zamiast 3). */
export const PARAGRAPH_FEATURES_2COL_LAYOUT = 75;

/** Pricing tiers - tytul + lead + 2 karty pakietow (opcjonalny "najczesciej wybierany" badge) +
 *  footnote. Kazda karta: eyebrow + price + subtitle + checklist cech + CTA. */
export const PARAGRAPH_PRICING_TIERS_LAYOUT = 76;

/** Integrations grid - tytul + lead + grid 2x2 kategorii integracji (kazda z pills). */
export const PARAGRAPH_INTEGRATIONS_GRID_LAYOUT = 77;

/** Two paths - eyebrow + tytul + grid 2 kart porownawczych {icon, name, desc}
 *  (np. "Wybierz swoja sciezke: Nowy sklep vs Migracja"). */
export const PARAGRAPH_TWO_PATHS_LAYOUT = 78;

/** Dark features - dark full-bleed section: zielony badge z dotem + tytul + lead +
 *  grid 3x3 kart {icon, name, desc}. Wariant why-us (39) z dark theme + badge. */
export const PARAGRAPH_DARK_FEATURES_LAYOUT = 79;

/** Services - light full-bleed mint section: eyebrow + tytul + lead (lewa) + pill
 *  CTA (prawa) + grid 3-kol 6 kart {icon, name, desc, href}. Klasyczny "nasze uslugi". */
export const PARAGRAPH_SERVICES_LAYOUT = 88;

/** Text + mini cards - light full-bleed mint section: 2-kol - text+CTA z lewej,
 *  grid 2x2 mini-kart {icon, name, href} z prawej (bez desc). Wariant services (45)
 *  bardziej kompaktowy, do lead-generation sekcji. */
export const PARAGRAPH_TEXT_MINI_CARDS_LAYOUT = 89;

/** Features 4-col - H2 + grid 4 kart w rzedzie {icon, name, desc}. Wariant why-us
 *  kompaktowy do jednego rzedu (np. "Dlaczego warto z nami pracowac"). */
export const PARAGRAPH_FEATURES_4COL_LAYOUT = 81;

/** Gallery slider - eyebrow + H2 + lead + Swiper obrazow z tytulami. Zdjecia z images
 *  tab (imp_paragraph_image) + tytuly z data['items'][idx].name. Typowo "zycie w firmie". */
export const PARAGRAPH_GALLERY_SLIDER_LAYOUT = 82;

/** Hero product - light full-bleed + 2 kolumny (lewa: eyebrow+H1+desc+3 CTA+podpis,
 *  prawa: obrazek/screenshot). 3 CTA opcjonalne z badge per przycisk (np. "WKROTCE"). */
export const PARAGRAPH_HERO_PRODUCT_LAYOUT = 50;

/** Dark text + mini cards - dark full-bleed + 2 kolumny (lewa: image+eyebrow+H2,
 *  prawa: grid 2x2 kart z ikonami). Analog TEXT_MINI_CARDS (46) w dark variant. */
export const PARAGRAPH_DARK_TEXT_MINI_CARDS_LAYOUT = 51;

/** Light features 2x2 - jasne karty z ikonami w gridzie 2x2 + opcjonalny H2 header. */
export const PARAGRAPH_LIGHT_FEATURES_2X2_LAYOUT = 52;

/** Product variants selector - light full-bleed + 4 klikane karty wariantow (interaktywne),
 *  po wyborze pokazuje 2-kol content: screenshot (z images tab) + tresc + features + CTA.
 *  Client component (useState active variant). Animacje: pulse ikona reki + bounce strzalka. */
export const PARAGRAPH_PRODUCT_VARIANTS_SELECTOR_LAYOUT = 54;

/** Client logos - minimalna sekcja "logo wall": eyebrow (text_1) + grid logotypow z images tab
 *  (nazwa klienta w description = alt). Responsywny grid 6 col -> 4 -> 2 -> 1. */
export const PARAGRAPH_CLIENT_LOGOS_LAYOUT = 55;

/** Split text + sticky CTA sidebar - 2 kolumny: lewa (intro + highlight + features + note),
 *  prawa (sticky sidebar z autorem + tytul + opis + duration + primary/secondary CTA + note).
 *  Sidebar uzywa position:sticky w obrebie kontenera - scrolluje się tylko w ramach layoutu, nie calej strony. */
export const PARAGRAPH_SPLIT_TEXT_STICKY_CTA_LAYOUT = 56;

/** Audience match ("Dla kogo") - eyebrow + tytul + intro + grid kart z zielona ikonka ✓ +
 *  opcjonalna karta wykluczen (heading + lista em-dash bullets). Ikona ta sama per grid (hardcoded). */
export const PARAGRAPH_AUDIENCE_MATCH_LAYOUT = 57;

/** Story card - 2 kolumny: lewa karta z badge/tytul/podtytul/HTML content/quote/CTA,
 *  prawa full-height image. Uniwersalny pattern dla case study/story spotlight'ow. */
export const PARAGRAPH_STORY_CARD_LAYOUT = 58;

/** Wezwanie do działania - treść i przyciski/boksy obok siebie albo jedno pod drugim,
 *  tło jasne, ciemne (pas na całą szerokość) albo morskie. Wchłonęło dawne 80 i 53. */
export const PARAGRAPH_LIGHT_CTA_BANNER_LAYOUT = 59;

/** Timeline steps - pionowa lista krokow wdrozenia. Kazda karta: numer (CSS counter)
 *  + tytul + opis + opcjonalny pill z czasem trwania (np. "1-2 tygodnie"). Bez limitu. */
export const PARAGRAPH_TIMELINE_STEPS_LAYOUT = 60;

/** Two models compare - 2 karty porownawcze (np. licencja vs SaaS). Kazda karta:
 *  tytul + pill badge obok tytulu + opis + lista bulletow z checkiem. Druga karta
 *  zawsze highlighted (accent styling - zielony border + mint bg). */
export const PARAGRAPH_TWO_MODELS_COMPARE_LAYOUT = 61;

/** Info notice - pojedyncza karta info: ikona Nucleo po lewej + heading (uppercase teal)
 *  + tresc. Uzycie: krotki call-out (np. "Usluga w przygotowaniu"). Bez H2. */
export const PARAGRAPH_INFO_NOTICE_LAYOUT = 62;

/** Metrics + checklist + image - 2-kol split. Lewa: eyebrow + H2 + desc + grid stat cards
 *  + checklist. Prawa: pojedyncze zdjecie (np. screenshot PageSpeed). */
export const PARAGRAPH_METRICS_CHECKLIST_IMAGE_LAYOUT = 63;

/** Benefits + Tools - 2-sekcyjny monster layout. Gora: 2-kol split (eyebrow+H2+lead lewa,
 *  lista 4 metric-cards z pill+name+desc prawa). Dol: dark full-bleed card z tytul+intro
 *  +grid 3-kol (do 6 tool-cards z badge+name+desc)+footer note. */
export const PARAGRAPH_BENEFITS_TOOLS_LAYOUT = 64;

/** Realizacje - grid kart wybranych realizacji (albo 3 najnowszych) + opcjonalny przycisk
 *  do portfolio. Do 2026-09-26 "Realizacje i opinie" - opinie klientów wydzielone do 91. */
export const PARAGRAPH_REALIZATIONS_LAYOUT = 65;

/** FAQ akordeon - portowany z okna w_dedicated_faq. Admin wpisuje pary Q/A w paragrafie,
 *  answer to HTML z TinyMCE. Frontend deleguje do WFaq komponentu. */
export const PARAGRAPH_FAQ_LAYOUT = 66;

/** Wpisy blogowe - grid 3 kart wybranych wpisow lub 3 najnowszych + optional
 *  see-all CTA. Analog do realizations+testimonials tylko dla c_blog. */
export const PARAGRAPH_BLOG_LATEST_LAYOUT = 67;

/** Inne rozwiazania - grid kart z dziecmi wybranego katalogu-rodzica. Karta:
 *  Nucleo icon + tytul + subtitle + CTA link do dziecka. */
export const PARAGRAPH_RELATED_CATALOGS_LAYOUT = 68;

/** Pasek ogloszen - dark full-bleed carousel z auto-rotacja (Swiper single-slide).
 *  Slajd: zielona pill + Nucleo icon + tekst + link. Dostepny tylko w w_descriptive_window. */
export const PARAGRAPH_ANNOUNCEMENT_BAR_LAYOUT = 69;

/** Dark hero z back-linkiem + kategoria - dark full-bleed section:
 *  back-pill (link do parent), zielona pigulka kategorii, H1 tytul, lead. */
export const PARAGRAPH_DARK_HERO_BACK_LAYOUT = 90;

/** Opinie klientów - karuzela opinii z wybranych realizacji (albo losowych), przewijana
 *  automatycznie co `autoplay_delay_ms` z pierścieniem postępu obok kropek. */
export const PARAGRAPH_CLIENT_TESTIMONIALS_LAYOUT = 91;

/**
 * Union literal typów wszystkich layoutów — używane przez discriminated
 * union `ParagraphData` jako wartość pola `layout`.
 */
export type ParagraphLayoutId =
  | typeof PARAGRAPH_TEXT_LAYOUT
  | typeof PARAGRAPH_TEXT_IMAGES_LEFT_LAYOUT
  | typeof PARAGRAPH_TEXT_IMAGES_RIGHT_LAYOUT
  | typeof PARAGRAPH_TEXT_IMAGES_TOP_LAYOUT
  | typeof PARAGRAPH_TEXT_IMAGES_BOT_LAYOUT
  | typeof PARAGRAPH_TWO_COLS_TEXT_LAYOUT
  | typeof PARAGRAPH_TWO_COLS_TEXT_VIDEO_LAYOUT
  | typeof PARAGRAPH_TWO_COLS_TABLE_TEXT_LAYOUT
  | typeof PARAGRAPH_TWO_COLS_TEXT_GALLERY_LAYOUT
  | typeof PARAGRAPH_PREZENTATION_LAYOUT
  | typeof PARAGRAPH_BIG_PHOTO_LAYOUT
  | typeof PARAGRAPH_THREE_COLS_TEXT_LAYOUT
  | typeof PARAGRAPH_THREE_COLS_TEXT_TEXT_PHOTO_LAYOUT
  | typeof PARAGRAPH_THREE_COLS_TEXT_PHOTO_TEXT_LAYOUT
  | typeof PARAGRAPH_THREE_COLS_PHOTO_TEXT_TEXT_LAYOUT
  | typeof PARAGRAPH_THREE_COLS_BIG_VIDEO_LAYOUT
  | typeof PARAGRAPH_TWO_BOXES_LAYOUT
  | typeof PARAGRAPH_THREE_BOXES_LAYOUT
  | typeof PARAGRAPH_CONTACT_FORM_LAYOUT
  | typeof PARAGRAPH_MAPS_LAYOUT
  | typeof PARAGRAPH_TWO_COLS_TEXT_PREZENTATION_LAYOUT
  | typeof PARAGRAPH_BLOG_POSTS_LAYOUT
  | typeof PARAGRAPH_CONTACT_PERSON_LAYOUT
  | typeof PARAGRAPH_HERO_CTA_LAYOUT
  | typeof PARAGRAPH_PROCESS_STEPS_LAYOUT
  | typeof PARAGRAPH_CTA_BANNER_LAYOUT
  | typeof PARAGRAPH_PRODUCT_CTA_LAYOUT
  | typeof PARAGRAPH_FEATURES_ICONS_LAYOUT
  | typeof PARAGRAPH_TEXT_STATS_LAYOUT
  | typeof PARAGRAPH_TECH_STACK_LAYOUT
  | typeof PARAGRAPH_SERVICE_HERO_LAYOUT
  | typeof PARAGRAPH_FEATURES_2COL_LAYOUT
  | typeof PARAGRAPH_PRICING_TIERS_LAYOUT
  | typeof PARAGRAPH_INTEGRATIONS_GRID_LAYOUT
  | typeof PARAGRAPH_TWO_PATHS_LAYOUT
  | typeof PARAGRAPH_DARK_FEATURES_LAYOUT
  | typeof PARAGRAPH_SERVICES_LAYOUT
  | typeof PARAGRAPH_TEXT_MINI_CARDS_LAYOUT
  | typeof PARAGRAPH_FEATURES_4COL_LAYOUT
  | typeof PARAGRAPH_GALLERY_SLIDER_LAYOUT
  | typeof PARAGRAPH_HERO_PRODUCT_LAYOUT
  | typeof PARAGRAPH_DARK_TEXT_MINI_CARDS_LAYOUT
  | typeof PARAGRAPH_LIGHT_FEATURES_2X2_LAYOUT
  | typeof PARAGRAPH_PRODUCT_VARIANTS_SELECTOR_LAYOUT
  | typeof PARAGRAPH_CLIENT_LOGOS_LAYOUT
  | typeof PARAGRAPH_SPLIT_TEXT_STICKY_CTA_LAYOUT
  | typeof PARAGRAPH_AUDIENCE_MATCH_LAYOUT
  | typeof PARAGRAPH_STORY_CARD_LAYOUT
  | typeof PARAGRAPH_LIGHT_CTA_BANNER_LAYOUT
  | typeof PARAGRAPH_TIMELINE_STEPS_LAYOUT
  | typeof PARAGRAPH_TWO_MODELS_COMPARE_LAYOUT
  | typeof PARAGRAPH_INFO_NOTICE_LAYOUT
  | typeof PARAGRAPH_METRICS_CHECKLIST_IMAGE_LAYOUT
  | typeof PARAGRAPH_BENEFITS_TOOLS_LAYOUT
  | typeof PARAGRAPH_REALIZATIONS_LAYOUT
  | typeof PARAGRAPH_FAQ_LAYOUT
  | typeof PARAGRAPH_BLOG_LATEST_LAYOUT
  | typeof PARAGRAPH_RELATED_CATALOGS_LAYOUT
  | typeof PARAGRAPH_ANNOUNCEMENT_BAR_LAYOUT
  | typeof PARAGRAPH_DARK_HERO_BACK_LAYOUT
  | typeof PARAGRAPH_CLIENT_TESTIMONIALS_LAYOUT;
